import React from 'react';
import { NodeIndexOutlined } from '@ant-design/icons';
import {
    Button,
    Text,
    TextAs,
    TextSizes,
    ButtonHierarchies,
} from '@clockout-dev/design-elements';
import SectionCard from '../../../common/sectionCard/SectionCard';
import LoadingSpinner from '../../../common/LoadingSpinner';
import UserStatePill from '../../../common/userStatePill/UserStatePill';
import UserFunnelRow from './UserFunnelRow';
import UserFunnelAttemptsModal from './UserFunnelAttemptsModal';
import {
    FUNNEL_ROW_TYPES,
    decorateFunnelMilestones,
    getFunnelEmptyState,
    getFunnelSummaryPills,
} from '../userFunnel.constants';
import styles from './styles/user-funnel-card.module.scss';

/**
 * "Onboarding Journey" card — the per-user funnel from
 * `GET /api/admin/users/:aba/:client_id/funnel`, rendered as a vertical
 * timeline so an agent can see which step the member is standing on, what
 * error they hit, and whether it was ever resolved.
 *
 * The header pills answer the question first ("Current step: …", plus the
 * error state when there is one); the timeline below is the evidence.
 *
 * The endpoint is backed by an Athena scan and is fetched on its own, so this
 * card owns its loading and failure states instead of leaning on the page
 * shell — the rest of the user detail is already on screen while this is
 * still running.
 *
 * @param {Object} props
 * @param {Object | null} [props.funnel] Funnel payload.
 * @param {boolean} [props.loading]
 * @param {string | null} [props.error] Message to show instead of the timeline.
 * @param {() => void} [props.onRetry]
 * @param {boolean} [props.attemptsOpen] Failure-history modal is open.
 * @param {Array<Object> | null} [props.attempts]
 * @param {boolean} [props.attemptsHasError] The endpoint's own `has_error`.
 * @param {boolean} [props.attemptsLoading]
 * @param {string | null} [props.attemptsError]
 * @param {() => void} [props.onOpenAttempts] Absent = the row does not open.
 * @param {() => void} [props.onCloseAttempts]
 * @param {(() => void) | null} [props.onRetryAttempts] Absent = the attempts
 *   failure is not one a retry can fix.
 */
function UserFunnelCard({
    funnel,
    loading,
    error,
    onRetry,
    attemptsOpen = false,
    attempts = null,
    attemptsHasError = false,
    attemptsLoading = false,
    attemptsError = null,
    onOpenAttempts = null,
    onCloseAttempts = null,
    onRetryAttempts = null,
}) {
    const rows = decorateFunnelMilestones(funnel?.milestones);
    const summaryPills = getFunnelSummaryPills(funnel);
    const currentStage = funnel?.current_stage || null;

    // The backend's own `has_events`, not a guess at the rows. The funnel
    // ALWAYS returns nine milestones, so the `!rows.length` this used to test
    // was unreachable for any successful response — a member with no events
    // got nine grey rows and never the empty state this card has always had
    // copy for.
    const empty = getFunnelEmptyState(funnel);
    // A scan that found nothing can still have resolved the Postgres-sourced
    // "first advance taken" row. That row is real data and the discrepancy is
    // itself the diagnostic, so the timeline stays under the notice rather
    // than being replaced by it.
    const showTimeline = Boolean(rows.length) && (!empty || empty.diagnostic);

    const header =
        summaryPills.length && !loading && !error ? (
            <div className={styles.headerPills}>
                {summaryPills.map((pill) => (
                    <UserStatePill key={pill.key} tone={pill.tone} label={pill.label} />
                ))}
            </div>
        ) : null;

    return (
        <SectionCard icon={<NodeIndexOutlined />} title="Onboarding Journey" action={header}>
            {loading ? <LoadingSpinner size="small" text="Building this member's journey…" inline /> : null}

            {!loading && error ? (
                <div className={styles.state}>
                    <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.stateText}>
                        {error}
                    </Text>
                    {onRetry ? (
                        <Button
                            hierarchy={ButtonHierarchies.SECONDARY}
                            onClick={onRetry}
                        >
                            Try again
                        </Button>
                    ) : null}
                </div>
            ) : null}

            {!loading && !error && (empty || !rows.length) ? (
                <div className={styles.state}>
                    <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.stateText}>
                        {empty ? empty.message : 'No journey events recorded for this member yet.'}
                    </Text>
                </div>
            ) : null}

            {!loading && !error && showTimeline ? (
                <ol className={styles.timeline}>
                    {rows.map((row, index) => (
                        <UserFunnelRow
                            key={`${row.type}-${row.key}`}
                            row={row}
                            isCurrent={row.type === FUNNEL_ROW_TYPES.MILESTONE && row.key === currentStage}
                            isLast={index === rows.length - 1}
                            // Only the error row opens, and only when the page
                            // gave us something to open it with.
                            onOpen={row.type === FUNNEL_ROW_TYPES.ERROR ? onOpenAttempts : null}
                        />
                    ))}
                </ol>
            ) : null}

            <UserFunnelAttemptsModal
                open={attemptsOpen}
                attempts={attempts}
                hasError={attemptsHasError}
                loading={attemptsLoading}
                error={attemptsError}
                onClose={onCloseAttempts}
                onRetry={onRetryAttempts}
            />
        </SectionCard>
    );
}

export default UserFunnelCard;
